import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Text, Flex, Spinner, Link } from '@chakra-ui/react';
import GlassCard from './GlassCard';
import ProjectedLineup from './ProjectedLineup';
import { usePotentialLineup } from '../hooks/useLineups';
import { getTeamInfoByAbbreviation } from '../utils/getTeamInfo';
import { useUserContext } from '../contexts/UserContext';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { usePopulatedLeague } from '../hooks/usePopulatedLeague';
import { useNextTeamGame } from '../hooks/useGames';
import { buildLineupsFromPotentialResponse } from '../utils/projectedLineupUtils';
import dayjs from 'dayjs';
import type { BannerGameSelection } from './ScheduleBanner';

/**
 * Dashboard panel showing the projected lineup for the user's next scheduled game.
 * Uses useNextTeamGame → team ID resolution → usePotentialLineup → ProjectedLineup.
 */
interface NextGameLineupPanelProps {
    overrideGame?: BannerGameSelection | null;
}

const NextGameLineupPanel: React.FC<NextGameLineupPanelProps> = ({ overrideGame }) => {
    const { myTeam } = useUserContext();
    const myLeague = useMyLeagueStore((state) => state.myLeague);
    const leagueId = myLeague?.id;

    const { data: league } = usePopulatedLeague(leagueId);

    // Find the user's team abbreviation from the league data
    const teamAbbr = useMemo(() => {
        if (!league || !myTeam) return undefined;
        let abbr: string | undefined;
        league.conferences?.forEach((conf: any) => {
            conf.divisions?.forEach((div: any) => {
                div.teams?.forEach((team: any) => {
                    if (team.id === myTeam) {
                        abbr = team.rw_abbreviation || team.abbreviation;
                    }
                });
            });
        });
        return abbr;
    }, [league, myTeam]);

    const { nextGame, isLoading: isLoadingNext } = useNextTeamGame(leagueId, teamAbbr);

    // Build abbreviation → team id map
    const teamAbbrToIdMap = useMemo(() => {
        if (!league) return new Map<string, number>();
        const map = new Map<string, number>();
        league.conferences?.forEach((conf: any) => {
            conf.divisions?.forEach((div: any) => {
                div.teams?.forEach((team: any) => {
                    const abbr = team.rw_abbreviation || team.abbreviation;
                    if (abbr) map.set(abbr, team.id);
                    if (team.abbreviation && team.abbreviation !== team.rw_abbreviation) {
                        map.set(team.abbreviation, team.id);
                    }
                });
            });
        });
        return map;
    }, [league]);

    const awayTeamId = nextGame ? teamAbbrToIdMap.get(nextGame.away_abbr) : undefined;
    const homeTeamId = nextGame ? teamAbbrToIdMap.get(nextGame.home_abbr) : undefined;

    // If overrideGame is provided, use it instead of the auto-detected nextGame
    const effectiveGame = overrideGame
        ? {
            awayAbbr: overrideGame.awayAbbr,
            homeAbbr: overrideGame.homeAbbr,
            awayTeamId: teamAbbrToIdMap.get(overrideGame.awayAbbr),
            homeTeamId: teamAbbrToIdMap.get(overrideGame.homeAbbr),
            leagueId,
            gameDate: overrideGame.gameDate,
        }
        : nextGame
            ? {
                awayAbbr: nextGame.away_abbr,
                homeAbbr: nextGame.home_abbr,
                awayTeamId,
                homeTeamId,
                leagueId,
                gameDate: nextGame.scheduled_date,
            }
            : null;

    const awayInfo = effectiveGame ? getTeamInfoByAbbreviation(effectiveGame.awayAbbr, league) : null;
    const homeInfo = effectiveGame ? getTeamInfoByAbbreviation(effectiveGame.homeAbbr, league) : null;

    const { data, isLoading: isLoadingLineup } = usePotentialLineup(
        effectiveGame?.awayTeamId,
        effectiveGame?.homeTeamId,
        effectiveGame?.leagueId,
        effectiveGame?.gameDate,
        {
            enabled: !!effectiveGame?.awayTeamId && !!effectiveGame?.homeTeamId,
        }
    );

    const { awayLineup, homeLineup, awayStarter, homeStarter } = buildLineupsFromPotentialResponse(
        data ?? undefined
    );

    if (!myTeam || !myLeague) return null;

    const isLoading = (overrideGame ? false : isLoadingNext) || isLoadingLineup;
    const dateLabel = effectiveGame
        ? dayjs(effectiveGame.gameDate).format('ddd, MMM D, YYYY')
        : '';

    return (
        <Box w="100%">
            <GlassCard p={4} color="white">
                <Flex
                    justify="space-between"
                    align="center"
                    gap={3}
                    mb={2}
                    flexWrap="wrap"
                    rowGap={1}
                >
                    <Text fontWeight="bold" fontSize="lg" textAlign="left" flex="1" minW={0}>
                        {effectiveGame
                            ? `${overrideGame ? 'Game' : 'Next Game'}: ${effectiveGame.awayAbbr} at ${effectiveGame.homeAbbr} — ${dateLabel} @ 2:00 PM PST`
                            : 'Next Game Lineup'}
                    </Text>
                    <Text
                        fontWeight="bold"
                        fontSize="lg"
                        textAlign="right"
                        flexShrink={0}
                        color="whiteAlpha.600"
                    >
                        Projected Lineups
                    </Text>
                </Flex>

                {isLoading ? (
                    <Flex justify="center" py={8}>
                        <Spinner size="lg" color="white" />
                    </Flex>
                ) : !effectiveGame ? (
                    <Text color="gray.400" fontSize="sm" textAlign="left">
                        No upcoming games on the schedule.
                    </Text>
                ) : (
                    <ProjectedLineup
                        awayAbbr={effectiveGame.awayAbbr}
                        homeAbbr={effectiveGame.homeAbbr}
                        awayTeamColor={awayInfo?.color_primary}
                        homeTeamColor={homeInfo?.color_primary}
                        awayTeamInfo={awayInfo ?? undefined}
                        homeTeamInfo={homeInfo ?? undefined}
                        awayLineup={awayLineup}
                        homeLineup={homeLineup}
                        awayStarter={awayStarter || undefined}
                        homeStarter={homeStarter || undefined}
                    />
                )}

                <Flex
                    mt={3}
                    justify="space-between"
                    align="center"
                    gap={3}
                    flexWrap="wrap"
                    rowGap={2}
                >
                    <Text
                        fontSize="sm"
                        fontStyle="italic"
                        opacity={0.9}
                        textAlign="left"
                        flex="1"
                        minW={0}
                    >
                        * Stats are YTD Dominus Game Stats
                    </Text>
                    <Link
                        as={RouterLink}
                        to="/league-schedule"
                        fontSize="sm"
                        fontWeight="medium"
                        color="green.300"
                        textAlign="right"
                        flexShrink={0}
                        _hover={{ color: 'green.200', textDecoration: 'underline' }}
                    >
                        See Schedule
                    </Link>
                </Flex>
            </GlassCard>
        </Box>
    );
};

export default NextGameLineupPanel;
